import {
  formatSiteDateTimeLocalInput,
  formatSiteTime,
  parseSiteDateTimeLocalInput,
} from '../siteTime/siteTime';

export const BOOTH_RECENT_EVIDENCE_SITE_ID = 'yyz';
export const BOOTH_RECENT_EVIDENCE_DEVICE_ID = 'demo5';
export const BOOTH_RECENT_EVIDENCE_CAMERA_ID = 'cam_a1bc71b70d77';
export const BOOTH_RECENT_EVIDENCE_ZONE_ID = 'rae_open_new_qual';
export const BOOTH_RECENT_EVIDENCE_POLICY_ID = 'pol_zone_intrusion_cbcf5066';
/** yyz customer site zone. The Ask demo page's America/Toronto zone does not apply here. */
export const BOOTH_RECENT_EVIDENCE_TIME_ZONE = 'America/Vancouver';
export const BOOTH_RECENT_EVIDENCE_MAX_ITEMS = 2;

export type BoothRecentEvidenceState = 'playable' | 'pending' | 'unavailable' | 'none';

export const BOOTH_RECENT_EVIDENCE_STATE_LABEL: Record<BoothRecentEvidenceState, string> = {
  playable: 'Evidence available',
  pending: 'Evidence processing',
  unavailable: 'Evidence unavailable',
  none: 'No clip retained',
};

export type BoothRecentEvidenceEpisode = {
  episodeId: string;
  qualifiedAt: string;
  startedAt?: string | null;
  endedAt?: string | null;
  status?: string | null;
  parentIncidentId?: string | null;
  physicalOccurrenceId?: string | null;
  evidenceState?: string | null;
  durationMs?: number | null;
  siteId?: string | null;
  deviceId?: string | null;
  cameraId?: string | null;
  zoneId?: string | null;
  policyId?: string | null;
};

export type BoothMeasurementStatus = 'finalized-measured' | 'finalized-limited' | 'pending';

export const BOOTH_MEASUREMENT_STATUS_LABEL: Record<BoothMeasurementStatus, string> = {
  'finalized-measured': 'Finalized measured',
  'finalized-limited': 'Finalized coverage-limited',
  pending: 'Pending finalization',
};

export type BoothRecentEvidenceItem = {
  episodeId: string;
  parentIncidentId: string | null;
  physicalOccurrenceId: string | null;
  eventTime: string;
  timeLabel: string;
  durationMs: number | null;
  evidenceState: BoothRecentEvidenceState;
  stateLabel: string;
  href: string | null;
  zoneLabel: string;
  cameraLabel: string;
  measurementStatus: BoothMeasurementStatus;
};

export type BoothEvidenceOccurrence = BoothRecentEvidenceItem & {
  episodeIds: string[];
};

export type BoothRecentEvidenceView = {
  status: 'loading' | 'ready' | 'empty' | 'unavailable';
  items: BoothRecentEvidenceItem[];
  occurrences?: BoothEvidenceOccurrence[];
  recordedCount?: number;
};

export function isBoothRecentEvidenceEnabled(search: string): boolean {
  return new URLSearchParams(search).get('recent_evidence') !== '0';
}

function nextCalendarDate(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  const next = new Date(Date.UTC(year, month - 1, day + 1));
  return [
    next.getUTCFullYear(),
    String(next.getUTCMonth() + 1).padStart(2, '0'),
    String(next.getUTCDate()).padStart(2, '0'),
  ].join('-');
}

export function boothRecentEvidenceDayBoundary(
  nowMs: number,
  timeZone?: string | null,
  localDay?: string | null,
): { occurredFrom: string; occurredTo: string } {
  const zone = timeZone || BOOTH_RECENT_EVIDENCE_TIME_ZONE;
  const local = formatSiteDateTimeLocalInput(new Date(nowMs).toISOString(), zone);
  const requested = String(localDay || '').trim();
  const date = /^\d{4}-\d{2}-\d{2}$/.test(requested) ? requested : local.slice(0, 10);
  return {
    occurredFrom: parseSiteDateTimeLocalInput(`${date}T00:00`, zone)!,
    occurredTo: parseSiteDateTimeLocalInput(`${nextCalendarDate(date)}T00:00`, zone)!,
  };
}

export function boothRecentEvidenceHref(incidentId: string): string {
  return `/incidents?site=${BOOTH_RECENT_EVIDENCE_SITE_ID}`
    + `&incident_id=${encodeURIComponent(incidentId)}`
    + '&review=1&exact_incident=1';
}

function scopeMatches(episode: BoothRecentEvidenceEpisode, expected: string, value?: string | null): boolean {
  const actual = String(value || '').trim();
  return !actual || actual === expected;
}

function completed(episode: BoothRecentEvidenceEpisode): boolean {
  const status = String(episode.status || '').trim().toLowerCase();
  if (status === 'cleared' || status === 'closed') return true;
  return Boolean(String(episode.endedAt || '').trim());
}

function evidenceState(value?: string | null): BoothRecentEvidenceState | null {
  if (value === 'playable' || value === 'pending' || value === 'unavailable' || value === 'none') {
    return value;
  }
  return null;
}

function durationMs(episode: BoothRecentEvidenceEpisode): number | null {
  if (episode.durationMs != null) {
    return typeof episode.durationMs === 'number' && Number.isFinite(episode.durationMs) && episode.durationMs >= 0
      ? episode.durationMs
      : null;
  }
  const started = Date.parse(String(episode.startedAt || ''));
  const ended = Date.parse(String(episode.endedAt || ''));
  if (!Number.isFinite(started) || !Number.isFinite(ended) || ended < started) return null;
  return ended - started;
}

export function buildBoothRecentEvidenceItems(
  episodes: BoothRecentEvidenceEpisode[],
  args: {
    timeZone?: string | null;
    occurredFrom: string;
    occurredTo: string;
    siteId?: string | null;
    deviceId?: string | null;
    cameraId?: string | null;
    zoneId?: string | null;
    policyId?: string | null;
    zoneLabel?: string | null;
    cameraLabel?: string | null;
    limit?: number | null;
  },
): BoothRecentEvidenceItem[] {
  const fromMs = Date.parse(args.occurredFrom);
  const toMs = Date.parse(args.occurredTo);
  const siteId = args.siteId || BOOTH_RECENT_EVIDENCE_SITE_ID;
  const deviceId = args.deviceId || BOOTH_RECENT_EVIDENCE_DEVICE_ID;
  const cameraId = args.cameraId || BOOTH_RECENT_EVIDENCE_CAMERA_ID;
  const zoneId = args.zoneId || BOOTH_RECENT_EVIDENCE_ZONE_ID;
  const policyId = args.policyId || BOOTH_RECENT_EVIDENCE_POLICY_ID;
  const chosen = new Map<string, BoothRecentEvidenceEpisode>();
  episodes.forEach((episode) => {
    const episodeId = String(episode.episodeId || '').trim();
    const qualifiedMs = Date.parse(String(episode.qualifiedAt || ''));
    const state = evidenceState(episode.evidenceState);
    if (
      !episodeId
      || !state
      || !completed(episode)
      || !Number.isFinite(qualifiedMs)
      || qualifiedMs < fromMs
      || qualifiedMs >= toMs
      || !scopeMatches(episode, siteId, episode.siteId)
      || !scopeMatches(episode, deviceId, episode.deviceId)
      || !scopeMatches(episode, cameraId, episode.cameraId)
      || !scopeMatches(episode, zoneId, episode.zoneId)
      || !scopeMatches(episode, policyId, episode.policyId)
    ) {
      return;
    }
    const previous = chosen.get(episodeId);
    if (!previous || qualifiedMs > Date.parse(String(previous.qualifiedAt || ''))) {
      chosen.set(episodeId, episode);
    }
  });
  const ordered = Array.from(chosen.values())
    .sort((left, right) => Date.parse(String(right.qualifiedAt)) - Date.parse(String(left.qualifiedAt)));
  const limited = args.limit === null ? ordered : ordered.slice(0, args.limit ?? BOOTH_RECENT_EVIDENCE_MAX_ITEMS);
  const zoneLabel = String(args.zoneLabel || '').trim() || 'area name unavailable';
  const cameraLabel = String(args.cameraLabel || '').trim() || 'camera name unavailable';
  return limited.map((episode) => {
      const state = evidenceState(episode.evidenceState)!;
      const parentIncidentId = String(episode.parentIncidentId || '').trim() || null;
      const physicalOccurrenceId = String(episode.physicalOccurrenceId || '').trim() || null;
      const eventTime = String(episode.qualifiedAt);
      return {
        episodeId: episode.episodeId,
        parentIncidentId,
        physicalOccurrenceId,
        eventTime,
        timeLabel: formatSiteTime(eventTime, args.timeZone || BOOTH_RECENT_EVIDENCE_TIME_ZONE),
        durationMs: durationMs(episode),
        evidenceState: state,
        stateLabel: BOOTH_RECENT_EVIDENCE_STATE_LABEL[state],
        href: state === 'playable' && parentIncidentId ? boothRecentEvidenceHref(parentIncidentId) : null,
        zoneLabel,
        cameraLabel,
        measurementStatus: 'pending' as const,
      };
    });
}

export function groupBoothEvidenceOccurrences(items: BoothRecentEvidenceItem[]): BoothEvidenceOccurrence[] {
  const groups = new Map<string, BoothEvidenceOccurrence>();
  items.forEach((item) => {
    const key = item.physicalOccurrenceId || item.episodeId;
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, { ...item, episodeIds: [item.episodeId] });
      return;
    }
    if (!existing.episodeIds.includes(item.episodeId)) existing.episodeIds.push(item.episodeId);
    if (item.href && !existing.href) existing.href = item.href;
    if (item.evidenceState === 'playable') existing.evidenceState = 'playable';
    existing.stateLabel = BOOTH_RECENT_EVIDENCE_STATE_LABEL[existing.evidenceState];
  });
  return Array.from(groups.values()).sort(
    (left, right) => Date.parse(right.eventTime) - Date.parse(left.eventTime),
  );
}

export function evidenceHeading(recordedCount: number, shownCount: number): string {
  const recorded = Math.max(0, recordedCount);
  const shown = Math.min(Math.max(0, shownCount), recorded);
  return `Recent evidence · Showing latest ${shown} of ${recorded} recorded episodes`;
}
